import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  isAuthenticated,
  logout as logoutUtil,
  isLoginRequired,
  isForceLoginEnabled,
  clearLoginState
} from '../lib/auth';
import { hasErpBearerSession, clearErpBearerToken } from '../lib/erpSessionAuth';

const AuthContext = createContext(null);

/**
 * AuthProvider - Manages authentication state throughout the application
 * Supports both persistent sessions (1-hour) and force login mode (login on every refresh)
 */
export const AuthProvider = ({ children }) => {
  /**
   * Frontend-only: disable BK Design UI login gate.
   * Auth is enforced by Frappe/ERP endpoints; this just avoids showing the local Login screen.
   */
  const [isLoggedIn, setIsLoggedIn] = useState(true);

  // Check authentication status on mount and set up interval
  useEffect(() => {
    setIsLoggedIn(true);
  }, []);

  // Handle successful login
  const handleLoginSuccess = useCallback(() => {
    setIsLoggedIn(true);
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    logoutUtil();
    clearErpBearerToken();
    setIsLoggedIn(false);
  }, []);

  const value = {
    isLoggedIn,
    login: handleLoginSuccess,
    logout: handleLogout,
    loginRequired: false,
    forceLoginEnabled: false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth hook - Access authentication context
 * @returns {Object} Authentication context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
